import React, { useEffect, useState } from "react";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { useLang } from "../context/LanguageContext";

// Ordered list matching the user-supplied brand design PDF
const DEFAULT_BRANDS = [
  { name: "General", src: "/brands/general.png" },
  { name: "Milon", src: "/brands/milon.png" },
  { name: "Lorenza", src: "/brands/lorenza.png" },
  { name: "Viva", src: "/brands/viva.png" },
  { name: "Comfee", src: "/brands/comfee.png" },
  { name: "Danford", src: "/brands/danford.png" },
  { name: "Weston", src: "/brands/weston.png" },
  { name: "Blanc", src: "/brands/blanc.png" },
];

/**
 * Seamless pure-CSS infinite marquee.
 *
 * We render the `items` list TWICE inside a flex row (width: max-content).
 * The CSS animation translates from 0% to -50% (or reverse for RTL), which
 * scrolls exactly one copy's worth of distance. Because the two copies are
 * identical, the handoff between the end and the start of a new loop is
 * pixel-perfect and completely gapless.
 */
function Strip({ items, invert, durationSec, direction, testId }) {
  // Triple the list for gapless wrap on any viewport width (incl. 4K)
  const loop = [...items, ...items, ...items];

  // Hold rendering until every unique logo image has loaded, so the
  // first visible paint is ALWAYS a fully populated strip — never a black gap.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!items || items.length === 0) {
      setReady(true);
      return;
    }
    let cancelled = false;
    let remaining = items.length;
    const done = () => {
      remaining -= 1;
      if (!cancelled && remaining <= 0) setReady(true);
    };
    items.forEach((b) => {
      const img = new Image();
      img.onload = done;
      img.onerror = done; // never let a broken url block the strip forever
      img.src = b.src;
    });
    // Safety fallback — always show within 1.5s even if network stalls
    const timer = setTimeout(() => !cancelled && setReady(true), 1500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [items]);

  return (
    <div
      className={`shams-marquee ${invert ? "bg-ink" : "bg-white border-y border-black/10"}`}
      data-testid={testId}
    >
      <div
        className={`shams-marquee-track ${direction === "right" ? "dir-right" : "dir-left"}`}
        style={{
          animationDuration: `${durationSec}s`,
          opacity: ready ? 1 : 0,
          transition: "opacity 0.25s ease-out",
        }}
        aria-hidden="true"
      >
        {loop.map((b, i) => (
          <div
            key={i}
            className="flex items-center justify-center w-36 sm:w-44 lg:w-48 h-20 shrink-0"
          >
            <img
              src={b.src}
              alt={b.name}
              draggable={false}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className="max-h-10 sm:max-h-12 w-auto max-w-[80%] object-contain opacity-85 hover:opacity-100 transition-opacity"
              style={invert ? { filter: "brightness(0) invert(1)" } : undefined}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export function BrandMarqueeDark() {
  const { settings } = useSiteSettings() || {};
  const { isRTL } = useLang();
  const items = resolveItems(settings);
  return (
    <Strip
      items={items}
      invert
      durationSec={32}
      direction={isRTL ? "right" : "left"}
      testId="brand-marquee-dark"
    />
  );
}

export function BrandMarqueeLight() {
  const { settings } = useSiteSettings() || {};
  const { isRTL } = useLang();
  const items = resolveItems(settings);
  return (
    <Strip
      items={items}
      invert={false}
      durationSec={40}
      direction={isRTL ? "left" : "right"}
      testId="brand-marquee-light"
    />
  );
}

function resolveItems(settings) {
  const raw = settings?.marquee;
  if (raw && raw.length > 0) {
    return raw
      .map((entry) => {
        if (!entry) return null;
        if (typeof entry === "string") {
          const slug = entry.toLowerCase().trim();
          return { name: entry, src: `/brands/${slug}.png` };
        }
        return { name: entry.name || "", src: entry.src || entry.url || "" };
      })
      .filter((x) => x && x.src);
  }
  return DEFAULT_BRANDS;
}

// Default export: both strips stacked (legacy use). Prefer importing the
// named components above to place each strip independently.
export default function BrandMarquee() {
  return (
    <div data-testid="brand-marquee">
      <BrandMarqueeDark />
      <BrandMarqueeLight />
    </div>
  );
}
